import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Type, Image, MousePointer, Minus, MoveVertical, Columns,
  ArrowLeft, Save, Trash2, ChevronUp, ChevronDown, Upload, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── MERGE VARIABLES ───
const MERGE_VARIABLES = [
  { tag: '{{name}}', label: 'Full Name', desc: 'Contact full name' },
  { tag: '{{first_name}}', label: 'First Name', desc: 'Contact first name' },
  { tag: '{{email}}', label: 'Email', desc: 'Contact email' },
  { tag: '{{phone}}', label: 'Phone', desc: 'Contact phone' },
  { tag: '{{business_name}}', label: 'Business', desc: 'Business name' },
  { tag: '{{service}}', label: 'Service', desc: 'Service purchased' },
  { tag: '{{products}}', label: 'Products', desc: 'Product names list' },
  { tag: '{{total_value}}', label: 'Total Value', desc: 'Sum of product values' },
  { tag: '{{id_number}}', label: 'ID Number', desc: 'Contact ID number' },
  { tag: '{{language}}', label: 'Language', desc: 'Contact language' },
];

// ─── COLUMN SUB-BLOCK TYPES ───
const COL_BLOCK_TYPES = [
  { type: 'heading', label: 'Heading', def: { text: 'Heading', fontSize: '20px', color: '#333', align: 'left' } },
  { type: 'text', label: 'Text', def: { text: 'Column text here...', fontSize: '14px', color: '#555', align: 'left' } },
  { type: 'image', label: 'Image', def: { src: '', alt: 'Image', width: '100%' } },
  { type: 'button', label: 'Button', def: { text: 'Click', url: '#', bgColor: '#00FF99', textColor: '#000' } },
];

const colSubBlockToHtml = (sb) => {
  const c = sb.content;
  switch (sb.type) {
    case 'heading': return `<h2 style="font-size:${c.fontSize};color:${c.color};text-align:${c.align};font-weight:bold;font-family:Arial,sans-serif;margin:0;padding:4px 0;">${c.text}</h2>`;
    case 'text': return `<p style="font-size:${c.fontSize};color:${c.color};text-align:${c.align};font-family:Arial,sans-serif;line-height:1.5;margin:0;padding:4px 0;">${c.text.replace(/\n/g,'<br/>')}</p>`;
    case 'image': return c.src ? `<img src="${c.src}" alt="${c.alt}" style="max-width:${c.width};height:auto;display:block;" />` : `<div style="color:#999;font-style:italic;padding:8px 0;">[ Image ]</div>`;
    case 'button': return `<table cellpadding="0" cellspacing="0" border="0" style="margin:6px 0;"><tr><td style="background:${c.bgColor};border-radius:4px;padding:8px 18px;"><a href="${c.url}" style="color:${c.textColor};text-decoration:none;font-weight:bold;font-family:Arial,sans-serif;font-size:13px;">${c.text}</a></td></tr></table>`;
    default: return '';
  }
};

// ─── MAIN BLOCK TYPES ───
const BLOCK_TYPES = [
  { type: 'heading', icon: Type, label: 'Heading', defaultContent: { text: 'Your Heading', fontSize: '28px', color: '#333333', align: 'center', fontWeight: 'bold' } },
  { type: 'text', icon: Type, label: 'Text', defaultContent: { text: 'Write your text here. You can add paragraphs and descriptions to your email.', fontSize: '16px', color: '#555555', align: 'left' } },
  { type: 'image', icon: Image, label: 'Image', defaultContent: { src: '', alt: 'Image', width: '100%', sourceType: 'url' } },
  { type: 'button', icon: MousePointer, label: 'Button', defaultContent: { text: 'Click Here', url: '#', bgColor: '#00FF99', textColor: '#000000', align: 'center', borderRadius: '4px' } },
  { type: 'divider', icon: Minus, label: 'Divider', defaultContent: { color: '#eeeeee', thickness: '1px' } },
  { type: 'spacer', icon: MoveVertical, label: 'Spacer', defaultContent: { height: '20px' } },
  { type: 'columns', icon: Columns, label: '2 Columns', defaultContent: { leftBlocks: [], rightBlocks: [] } },
  { type: 'logo', icon: Upload, label: 'Logo', defaultContent: { src: '', alt: 'Company Logo', width: '200px', align: 'center', sourceType: 'url' } },
];

const blockToHtml = (block) => {
  const c = block.content;
  switch (block.type) {
    case 'heading':
      return `<h1 style="font-size:${c.fontSize};color:${c.color};text-align:${c.align};font-weight:${c.fontWeight||'bold'};font-family:Arial,sans-serif;margin:0;padding:10px 0;">${c.text}</h1>`;
    case 'text':
      return `<p style="font-size:${c.fontSize};color:${c.color};text-align:${c.align};font-family:Arial,sans-serif;line-height:1.6;margin:0;padding:10px 0;">${c.text.replace(/\n/g,'<br/>')}</p>`;
    case 'image':
    case 'logo':
      if (!c.src) return `<div style="text-align:${c.align||'center'};padding:10px 0;color:#999;font-style:italic;">[ ${block.type === 'logo' ? 'Logo' : 'Image'} — add a URL or upload ]</div>`;
      return `<div style="text-align:${c.align||'center'};padding:10px 0;"><img src="${c.src}" alt="${c.alt}" style="max-width:${c.width||'100%'};height:auto;display:inline-block;" /></div>`;
    case 'button':
      return `<div style="text-align:${c.align};padding:10px 0;"><table cellpadding="0" cellspacing="0" border="0" style="margin:0 ${c.align==='center'?'auto':c.align==='right'?'0 0 auto':'auto 0'};"><tr><td style="background-color:${c.bgColor};border-radius:${c.borderRadius||'4px'};padding:12px 28px;"><a href="${c.url}" style="color:${c.textColor};text-decoration:none;font-weight:bold;font-family:Arial,sans-serif;font-size:14px;">${c.text}</a></td></tr></table></div>`;
    case 'divider':
      return `<hr style="border:none;border-top:${c.thickness} solid ${c.color};margin:10px 0;" />`;
    case 'spacer':
      return `<div style="height:${c.height};"></div>`;
    case 'columns': {
      const leftHtml = (c.leftBlocks||[]).map(colSubBlockToHtml).join('') || '<p style="color:#999;font-family:Arial,sans-serif;font-size:13px;">Left column</p>';
      const rightHtml = (c.rightBlocks||[]).map(colSubBlockToHtml).join('') || '<p style="color:#999;font-family:Arial,sans-serif;font-size:13px;">Right column</p>';
      return `<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td width="50%" style="padding:10px;vertical-align:top;">${leftHtml}</td><td width="50%" style="padding:10px;vertical-align:top;">${rightHtml}</td></tr></table>`;
    }
    default: return '';
  }
};

const generateFooterHtml = (footer, footerStyle) => {
  if (!footer) return '';
  const bg = footerStyle?.bgColor || '#1a1a1a';
  const fg = footerStyle?.textColor || '#cccccc';
  const linkColor = footerStyle?.textColor || '#cccccc';
  const name = footer.commercial_name || footer.name || '';
  const parts = [];
  if (name) parts.push(`<strong style="color:${fg};">${name}</strong>`);
  if (footer.address) parts.push(footer.address);
  const contact = [];
  if (footer.phone) contact.push(footer.phone);
  if (footer.email) contact.push(`<a href="mailto:${footer.email}" style="color:${linkColor};text-decoration:underline;">${footer.email}</a>`);
  if (contact.length) parts.push(contact.join(' | '));
  if (footer.website) parts.push(`<a href="${footer.website}" style="color:${linkColor};text-decoration:underline;">${footer.website}</a>`);
  if (parts.length === 0 && !footerStyle?.showUnsub) return '';
  let html = parts.join('<br/>');
  if (footerStyle?.showUnsub) {
    html += `<br/><br/><a href="{{UNSUBSCRIBE_URL}}" style="color:${linkColor};text-decoration:underline;font-size:11px;">Unsubscribe from this mailing list</a>`;
  }
  return `<div style="background-color:${bg};text-align:center;padding:24px 16px;font-size:12px;color:${fg};font-family:Arial,sans-serif;line-height:1.8;border-radius:0 0 4px 4px;">${html}</div>`;
};

const generateFullHtml = (blocks, footer, footerStyle) => {
  const inner = blocks.map(blockToHtml).join('');
  const footerHtml = generateFooterHtml(footer, footerStyle);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background-color:#f4f4f4;"><table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f4f4;"><tr><td align="center" style="padding:20px 0;"><table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:4px;overflow:hidden;"><tr><td style="padding:30px;">${inner}</td></tr>${footerHtml?`<tr><td style="padding:0;">${footerHtml}</td></tr>`:''}</table></td></tr></table></body></html>`;
};

// ─── Image Upload Helper ───
const ImageSourceInput = ({ src, sourceType, onChangeSrc, onChangeType, label }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChangeSrc(res.data.url);
      toast.success('Image uploaded');
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs text-[#B0B0B0]">{label || 'Image Source'}</Label>
      <div className="flex gap-1 mt-1">
        <button onClick={() => onChangeType('url')}
          className={`px-3 py-1 rounded text-xs ${sourceType === 'url' ? 'bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]' : 'bg-[#1A1A1A] text-[#B0B0B0] border border-[rgba(255,255,255,0.12)]'}`}>
          URL
        </button>
        <button onClick={() => onChangeType('upload')}
          className={`px-3 py-1 rounded text-xs ${sourceType === 'upload' ? 'bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]' : 'bg-[#1A1A1A] text-[#B0B0B0] border border-[rgba(255,255,255,0.12)]'}`}>
          Upload
        </button>
      </div>
      {sourceType === 'url' ? (
        <Input value={src} onChange={e => onChangeSrc(e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-sm" placeholder="https://..." />
      ) : (
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload}
            className="hidden" />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="w-full bg-[#1A1A1A] border border-[rgba(255,255,255,0.12)] text-[#B0B0B0] hover:text-white hover:border-[#00FF99] text-xs">
            <Upload size={12} className="mr-1" /> {uploading ? 'Uploading...' : 'Choose File'}
          </Button>
          {src && <p className="text-xs text-[#808080] mt-1 truncate">Uploaded: {src.split('/').pop()}</p>}
        </div>
      )}
    </div>
  );
};

// ─── Column Sub-block Editor in Settings ───
const ColumnBlocksEditor = ({ blocks, onChange, label }) => {
  const addSubBlock = (type) => {
    const def = COL_BLOCK_TYPES.find(t => t.type === type)?.def || {};
    onChange([...blocks, { type, content: { ...def }, id: `csb_${Date.now()}_${Math.random().toString(36).substr(2,5)}` }]);
  };
  const updateSubBlock = (idx, updated) => onChange(blocks.map((b, i) => i === idx ? updated : b));
  const removeSubBlock = (idx) => onChange(blocks.filter((_, i) => i !== idx));
  const moveSubBlock = (idx, dir) => {
    const arr = [...blocks]; const t = idx + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    onChange(arr);
  };

  return (
    <div>
      <Label className="text-xs text-[#B0B0B0] mb-1 block">{label}</Label>
      <div className="space-y-2 mb-2">
        {blocks.map((sb, idx) => (
          <div key={sb.id || idx} className="bg-[#1A1A1A] rounded p-2 border border-[rgba(255,255,255,0.08)]">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-[#808080] uppercase">{sb.type}</span>
              <div className="flex gap-0.5">
                <button onClick={() => moveSubBlock(idx, -1)} disabled={idx === 0} className="p-0.5 text-[#808080] disabled:opacity-20"><ChevronUp size={10} /></button>
                <button onClick={() => moveSubBlock(idx, 1)} disabled={idx === blocks.length - 1} className="p-0.5 text-[#808080] disabled:opacity-20"><ChevronDown size={10} /></button>
                <button onClick={() => removeSubBlock(idx)} className="p-0.5 text-[#808080] hover:text-[#FF3B30]"><Trash2 size={10} /></button>
              </div>
            </div>
            {(sb.type === 'heading' || sb.type === 'text') && (
              <Input value={sb.content.text} onChange={e => updateSubBlock(idx, { ...sb, content: { ...sb.content, text: e.target.value } })}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs h-7" />
            )}
            {sb.type === 'image' && (
              <Input value={sb.content.src} onChange={e => updateSubBlock(idx, { ...sb, content: { ...sb.content, src: e.target.value } })}
                className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs h-7" placeholder="Image URL" />
            )}
            {sb.type === 'button' && (
              <div className="flex gap-1">
                <Input value={sb.content.text} onChange={e => updateSubBlock(idx, { ...sb, content: { ...sb.content, text: e.target.value } })}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs h-7 flex-1" placeholder="Text" />
                <Input value={sb.content.url} onChange={e => updateSubBlock(idx, { ...sb, content: { ...sb.content, url: e.target.value } })}
                  className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs h-7 flex-1" placeholder="URL" />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {COL_BLOCK_TYPES.map(t => (
          <button key={t.type} onClick={() => addSubBlock(t.type)}
            className="text-[10px] px-2 py-1 rounded bg-[#1A1A1A] text-[#B0B0B0] hover:text-white border border-[rgba(255,255,255,0.08)] hover:border-[#00FF99]">
            + {t.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Settings Panel ───
const BlockSettings = ({ block, onChange }) => {
  const c = block.content;
  const update = (key, val) => onChange({ ...block, content: { ...c, [key]: val } });

  return (
    <div className="space-y-3">
      <h4 className="text-xs text-[#808080] uppercase tracking-widest font-bold">{block.type} Settings</h4>

      {(block.type === 'heading' || block.type === 'text') && (
        <>
          {block.type === 'heading' ? (
            <div>
              <Label className="text-xs text-[#B0B0B0]">Text</Label>
              <Input value={c.text} onChange={e => update('text', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-sm mt-1" />
            </div>
          ) : (
            <div>
              <Label className="text-xs text-[#B0B0B0]">Content</Label>
              <textarea value={c.text} onChange={e => update('text', e.target.value)}
                className="w-full bg-transparent border border-[rgba(255,255,255,0.12)] text-white text-sm rounded-md p-2 min-h-[100px] resize-y mt-1" />
            </div>
          )}
          {/* Merge Variables */}
          <div>
            <Label className="text-xs text-[#B0B0B0]">Insert Variable</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {MERGE_VARIABLES.map(v => (
                <button key={v.tag}
                  onClick={() => update('text', c.text + v.tag)}
                  title={v.desc}
                  data-testid={`insert-var-${v.tag.replace(/[{}]/g, '')}`}
                  className="px-2 py-1 rounded text-[10px] font-mono bg-[#1A1A1A] text-[#00FF99] border border-[rgba(255,255,255,0.08)] hover:border-[#00FF99] hover:bg-[#00FF99]/10 transition-colors duration-200">
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-[#B0B0B0]">Font Size</Label>
              <Input value={c.fontSize} onChange={e => update('fontSize', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0]">Color</Label>
              <div className="flex items-center gap-1 mt-1">
                <input type="color" value={c.color} onChange={e => update('color', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                <Input value={c.color} onChange={e => update('color', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs flex-1" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Alignment</Label>
            <div className="flex gap-1 mt-1">
              {['left','center','right'].map(a => (
                <button key={a} onClick={() => update('align', a)}
                  className={`px-3 py-1.5 rounded text-xs capitalize ${c.align===a?'bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]':'bg-[#1A1A1A] text-[#B0B0B0] border border-[rgba(255,255,255,0.12)]'}`}>{a}</button>
              ))}
            </div>
          </div>
        </>
      )}

      {(block.type === 'image' || block.type === 'logo') && (
        <>
          <ImageSourceInput
            src={c.src} sourceType={c.sourceType || 'url'}
            onChangeSrc={v => update('src', v)} onChangeType={v => update('sourceType', v)}
            label={block.type === 'logo' ? 'Logo Source' : 'Image Source'}
          />
          <div>
            <Label className="text-xs text-[#B0B0B0]">Alt Text</Label>
            <Input value={c.alt} onChange={e => update('alt', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs mt-1" />
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Width</Label>
            <Input value={c.width} onChange={e => update('width', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs mt-1" placeholder="100% or 200px" />
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Alignment</Label>
            <div className="flex gap-1 mt-1">
              {['left','center','right'].map(a => (
                <button key={a} onClick={() => update('align', a)}
                  className={`px-3 py-1.5 rounded text-xs capitalize ${(c.align||'center')===a?'bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]':'bg-[#1A1A1A] text-[#B0B0B0] border border-[rgba(255,255,255,0.12)]'}`}>{a}</button>
              ))}
            </div>
          </div>
        </>
      )}

      {block.type === 'button' && (
        <>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Button Text</Label>
            <Input value={c.text} onChange={e => update('text', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-sm mt-1" />
            <div className="flex flex-wrap gap-1 mt-1">
              {MERGE_VARIABLES.slice(0, 3).map(v => (
                <button key={v.tag} onClick={() => update('text', c.text + v.tag)} title={v.desc}
                  className="px-2 py-0.5 rounded text-[10px] font-mono bg-[#1A1A1A] text-[#00FF99] border border-[rgba(255,255,255,0.08)] hover:border-[#00FF99] transition-colors duration-200">{v.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Link URL</Label>
            <Input value={c.url} onChange={e => update('url', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-sm mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-[#B0B0B0]">Background</Label>
              <div className="flex items-center gap-1 mt-1">
                <input type="color" value={c.bgColor} onChange={e => update('bgColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                <Input value={c.bgColor} onChange={e => update('bgColor', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#B0B0B0]">Text Color</Label>
              <div className="flex items-center gap-1 mt-1">
                <input type="color" value={c.textColor} onChange={e => update('textColor', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                <Input value={c.textColor} onChange={e => update('textColor', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs flex-1" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Border Radius</Label>
            <Input value={c.borderRadius} onChange={e => update('borderRadius', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs mt-1" />
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Alignment</Label>
            <div className="flex gap-1 mt-1">
              {['left','center','right'].map(a => (
                <button key={a} onClick={() => update('align', a)}
                  className={`px-3 py-1.5 rounded text-xs capitalize ${c.align===a?'bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]':'bg-[#1A1A1A] text-[#B0B0B0] border border-[rgba(255,255,255,0.12)]'}`}>{a}</button>
              ))}
            </div>
          </div>
        </>
      )}

      {block.type === 'divider' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-[#B0B0B0]">Color</Label>
            <div className="flex items-center gap-1 mt-1">
              <input type="color" value={c.color} onChange={e => update('color', e.target.value)} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
              <Input value={c.color} onChange={e => update('color', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs flex-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#B0B0B0]">Thickness</Label>
            <Input value={c.thickness} onChange={e => update('thickness', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs mt-1" />
          </div>
        </div>
      )}

      {block.type === 'spacer' && (
        <div>
          <Label className="text-xs text-[#B0B0B0]">Height</Label>
          <Input value={c.height} onChange={e => update('height', e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs mt-1" />
        </div>
      )}

      {block.type === 'columns' && (
        <>
          <ColumnBlocksEditor
            blocks={c.leftBlocks || []}
            onChange={v => update('leftBlocks', v)}
            label="Left Column"
          />
          <div className="border-t border-[rgba(255,255,255,0.12)] pt-3 mt-3" />
          <ColumnBlocksEditor
            blocks={c.rightBlocks || []}
            onChange={v => update('rightBlocks', v)}
            label="Right Column"
          />
        </>
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ───
export default function EmailBuilderPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockIdx, setSelectedBlockIdx] = useState(null);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadTemplateOpen, setLoadTemplateOpen] = useState(false);
  const [businessFooter, setBusinessFooter] = useState(null);
  const [footerStyle, setFooterStyle] = useState({ bgColor: '#1a1a1a', textColor: '#cccccc', showUnsub: true });

  const fetchCampaign = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true });
      setCampaign(res.data);
      if (res.data.json_content) {
        try {
          const parsed = JSON.parse(res.data.json_content);
          if (parsed.blocks) { setBlocks(parsed.blocks); setFooterStyle(parsed.footerStyle || footerStyle); }
          else { setBlocks(parsed); }
        } catch { setBlocks([]); }
      }
      if (res.data.business_id) {
        try {
          const fRes = await axios.get(`${API}/businesses/${res.data.business_id}/footer`, { withCredentials: true });
          setBusinessFooter(fRes.data);
        } catch { /* no footer */ }
      }
    } catch { toast.error('Campaign not found'); navigate('/campaigns'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, navigate]);

  const fetchTemplates = useCallback(async () => {
    try { const res = await axios.get(`${API}/templates`, { withCredentials: true }); setTemplates(res.data); } catch {}
  }, []);

  useEffect(() => { fetchCampaign(); fetchTemplates(); }, [fetchCampaign, fetchTemplates]);

  const addBlock = (blockType) => {
    const newBlock = { id: `block_${Date.now()}_${Math.random().toString(36).substr(2,9)}`, type: blockType.type, content: { ...blockType.defaultContent } };
    if (blockType.type === 'columns') {
      newBlock.content = { leftBlocks: [], rightBlocks: [] };
    }
    setBlocks(prev => [...prev, newBlock]);
    setSelectedBlockIdx(blocks.length);
  };

  const updateBlock = (index, updated) => setBlocks(prev => prev.map((b, i) => i === index ? updated : b));
  const deleteBlock = (index) => { setBlocks(prev => prev.filter((_, i) => i !== index)); setSelectedBlockIdx(null); };
  const moveBlock = (index, direction) => {
    const nb = [...blocks]; const t = index + direction;
    if (t < 0 || t >= nb.length) return;
    [nb[index], nb[t]] = [nb[t], nb[index]];
    setBlocks(nb); setSelectedBlockIdx(t);
  };

  const saveCampaign = async () => {
    try {
      const html = generateFullHtml(blocks, businessFooter, footerStyle);
      const jsonContent = JSON.stringify({ blocks, footerStyle });
      await axios.put(`${API}/campaigns/${campaignId}`, { html_content: html, json_content: jsonContent }, { withCredentials: true });
      toast.success('Campaign saved');
    } catch { toast.error('Failed to save'); }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const html = generateFullHtml(blocks, businessFooter, footerStyle);
      const jsonContent = JSON.stringify({ blocks, footerStyle });
      await axios.post(`${API}/templates`, { name: templateName, html_content: html, json_content: jsonContent, category: 'custom' }, { withCredentials: true });
      toast.success('Template saved'); setSaveTemplateOpen(false); setTemplateName(''); fetchTemplates();
    } catch { toast.error('Failed to save template'); }
  };

  const loadTemplate = (tpl) => {
    if (tpl.json_content) {
      try {
        const parsed = JSON.parse(tpl.json_content);
        if (parsed.blocks) { setBlocks(parsed.blocks); setFooterStyle(parsed.footerStyle || footerStyle); }
        else { setBlocks(parsed); }
        setSelectedBlockIdx(null);
      } catch { toast.error('Invalid template'); }
    }
    setLoadTemplateOpen(false); toast.success('Template loaded');
  };

  const htmlPreview = useMemo(() => generateFullHtml(blocks, businessFooter, footerStyle), [blocks, businessFooter, footerStyle]);
  const selectedBlock = selectedBlockIdx !== null ? blocks[selectedBlockIdx] : null;

  return (
    <div className="flex flex-col h-screen bg-[#0A0A0A]" data-testid="email-builder-page">
      {/* Toolbar */}
      <div className="h-14 bg-[#0A0A0A] border-b border-[rgba(255,255,255,0.12)] flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/campaigns')} className="text-[#B0B0B0] hover:text-white transition-colors duration-200"><ArrowLeft size={18} /></button>
          <span className="text-white font-bold text-sm" style={{ fontFamily: 'Manrope' }}>{campaign?.name || 'Email Builder'}</span>
          {campaign?.subject && <span className="text-[#808080] text-xs ml-2">— {campaign.subject}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => setLoadTemplateOpen(true)} className="text-[#B0B0B0] hover:text-white text-xs">Load Template</Button>
          <Button size="sm" variant="ghost" onClick={() => setSaveTemplateOpen(true)} className="text-[#B0B0B0] hover:text-white text-xs">Save as Template</Button>
          <Button size="sm" onClick={saveCampaign} data-testid="save-campaign-btn" className="bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A] text-xs"><Save size={14} className="mr-1" /> Save</Button>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Block Palette + Layers */}
        <div className="w-52 bg-[#0A0A0A] border-r border-[rgba(255,255,255,0.12)] p-3 overflow-y-auto shrink-0">
          <h3 className="text-xs text-[#808080] uppercase tracking-widest mb-3 px-1">Add Blocks</h3>
          <div className="space-y-1.5">
            {BLOCK_TYPES.map(bt => (
              <button key={bt.type} onClick={() => addBlock(bt)} data-testid={`add-block-${bt.type}`}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-[#B0B0B0] hover:text-white hover:bg-[#1A1A1A] border border-[rgba(255,255,255,0.06)] transition-colors duration-200">
                <bt.icon size={14} strokeWidth={1.5} />{bt.label}
              </button>
            ))}
          </div>
          {blocks.length > 0 && (
            <>
              <h3 className="text-xs text-[#808080] uppercase tracking-widest mt-6 mb-3 px-1">Layers</h3>
              <div className="space-y-1">
                {blocks.map((block, idx) => (
                  <button key={block.id} onClick={() => setSelectedBlockIdx(idx)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors duration-200 ${selectedBlockIdx===idx?'bg-[#00FF99]/10 text-[#00FF99] border border-[#00FF99]/30':'text-[#B0B0B0] hover:text-white hover:bg-[#1A1A1A]'}`}>
                    <span className="capitalize truncate">{block.type}</span>
                    <div className="flex gap-0.5 shrink-0">
                      <button onClick={e=>{e.stopPropagation();moveBlock(idx,-1)}} disabled={idx===0} className="p-0.5 disabled:opacity-20"><ChevronUp size={10}/></button>
                      <button onClick={e=>{e.stopPropagation();moveBlock(idx,1)}} disabled={idx===blocks.length-1} className="p-0.5 disabled:opacity-20"><ChevronDown size={10}/></button>
                      <button onClick={e=>{e.stopPropagation();deleteBlock(idx)}} className="p-0.5 hover:text-[#FF3B30]"><Trash2 size={10}/></button>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* CENTER: Live Preview with move controls on hover */}
        <div className="flex-1 overflow-y-auto bg-[#2a2a2a] p-6">
          <div className="max-w-[640px] mx-auto">
            <div className="bg-[#f4f4f4] rounded-lg overflow-hidden shadow-lg">
              {blocks.length === 0 ? (
                <div className="bg-white p-12 text-center text-gray-400">
                  <Type size={36} className="mx-auto mb-4 opacity-30" />
                  <p className="text-base">Start building your email</p>
                  <p className="text-sm mt-1">Click blocks on the left to add content</p>
                </div>
              ) : (
                <div className="p-0">
                  <div className="bg-white" style={{ maxWidth: 600, margin: '0 auto' }}>
                    <div style={{ padding: '30px' }}>
                      {blocks.map((block, idx) => (
                        <div key={block.id} className="group relative"
                          onClick={() => setSelectedBlockIdx(idx)}>
                          {/* Move controls overlay */}
                          <div className="absolute -left-10 top-0 bottom-0 flex flex-col items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                            <button onClick={e => { e.stopPropagation(); moveBlock(idx, -1); }} disabled={idx === 0}
                              className="p-1 bg-[#0A0A0A] rounded text-white hover:bg-[#00FF99] hover:text-[#0A0A0A] disabled:opacity-20 shadow" title="Move up">
                              <ChevronUp size={14} />
                            </button>
                            <button onClick={e => { e.stopPropagation(); moveBlock(idx, 1); }} disabled={idx === blocks.length - 1}
                              className="p-1 bg-[#0A0A0A] rounded text-white hover:bg-[#00FF99] hover:text-[#0A0A0A] disabled:opacity-20 shadow" title="Move down">
                              <ChevronDown size={14} />
                            </button>
                            <button onClick={e => { e.stopPropagation(); deleteBlock(idx); }}
                              className="p-1 bg-[#0A0A0A] rounded text-white hover:bg-[#FF3B30] shadow" title="Delete">
                              <Trash2 size={12} />
                            </button>
                          </div>
                          <div className={`cursor-pointer transition-all duration-150 rounded relative ${
                            selectedBlockIdx === idx ? 'ring-2 ring-[#00FF99] ring-offset-2 ring-offset-white' : 'hover:ring-1 hover:ring-blue-300 hover:ring-offset-1 hover:ring-offset-white'
                          }`} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blockToHtml(block)) }} />
                        </div>
                      ))}
                    </div>
                    {/* Footer Preview */}
                    {businessFooter && (businessFooter.commercial_name || businessFooter.name || footerStyle.showUnsub) && (
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generateFooterHtml(businessFooter, footerStyle)) }} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Settings Panel */}
        <div className="w-72 bg-[#0A0A0A] border-l border-[rgba(255,255,255,0.12)] p-4 overflow-y-auto shrink-0">
          {selectedBlock ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs text-[#808080] uppercase tracking-widest">Properties</h3>
                <button onClick={() => deleteBlock(selectedBlockIdx)} className="p-1 text-[#808080] hover:text-[#FF3B30]"><Trash2 size={14} /></button>
              </div>
              <BlockSettings block={selectedBlock} onChange={updated => updateBlock(selectedBlockIdx, updated)} />
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-xs text-[#808080] mb-4">Click a block in the preview to edit</p>
            </div>
          )}

          {/* Footer Style Settings */}
          <div className="mt-6 pt-4 border-t border-[rgba(255,255,255,0.12)]">
            <h4 className="text-xs text-[#808080] uppercase tracking-widest mb-3 font-bold">Footer Settings</h4>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[#B0B0B0]">Background Color</Label>
                <div className="flex items-center gap-1 mt-1">
                  <input type="color" value={footerStyle.bgColor} onChange={e => setFooterStyle(p => ({...p, bgColor: e.target.value}))} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                  <Input value={footerStyle.bgColor} onChange={e => setFooterStyle(p => ({...p, bgColor: e.target.value}))} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs flex-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#B0B0B0]">Text Color</Label>
                <div className="flex items-center gap-1 mt-1">
                  <input type="color" value={footerStyle.textColor} onChange={e => setFooterStyle(p => ({...p, textColor: e.target.value}))} className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                  <Input value={footerStyle.textColor} onChange={e => setFooterStyle(p => ({...p, textColor: e.target.value}))} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white text-xs flex-1" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={footerStyle.showUnsub} onChange={e => setFooterStyle(p => ({...p, showUnsub: e.target.checked}))} className="accent-[#00FF99]" />
                <span className="text-xs text-[#B0B0B0]">Show Unsubscribe Link</span>
              </label>
              {businessFooter && (
                <p className="text-xs text-[#808080]">Business info auto-populated. Edit in Businesses page.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save/Load Template Dialogs */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-sm">
          <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Save as Template</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <Input value={templateName} onChange={e => setTemplateName(e.target.value)} className="bg-transparent border-[rgba(255,255,255,0.12)] text-white" placeholder="Template name" />
            <Button onClick={saveAsTemplate} className="w-full bg-[#00FF99] text-[#0A0A0A] font-bold hover:bg-[#00E68A]">Save Template</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={loadTemplateOpen} onOpenChange={setLoadTemplateOpen}>
        <DialogContent className="bg-[#121212] border-[rgba(255,255,255,0.12)] text-white max-w-md">
          <DialogHeader><DialogTitle style={{fontFamily:'Manrope'}}>Load Template</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2 max-h-60 overflow-y-auto">
            {templates.length===0 ? <p className="text-sm text-[#808080]">No templates saved yet.</p> :
              templates.map(t => (
                <button key={t.template_id} onClick={() => loadTemplate(t)}
                  className="w-full text-left px-3 py-2 rounded-md text-sm text-[#B0B0B0] hover:text-white hover:bg-[#1A1A1A] border border-[rgba(255,255,255,0.06)] transition-colors duration-200">{t.name}</button>
              ))
            }
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
